import { namespace } from 'wsm-tagging-utils';

const runRuntimeBuilder = (win) => {
	namespace(win, 'DDC.tracking.framework.config');

	const {
		DDC: { tracking },
		com,
		DataLayerHelper
	} = win;

	tracking.framework.config.RuntimeBuilder = (function buildRuntimeBuilder() {
		// ======================================================================
		// --------------- Private Static Variables and Functions ---------------
		// ======================================================================

		/**
		 * Maintains a set of unique components instantiated by all RuntimeBuilders.
		 * Components are stored using their class name as key, with each key containing
		 * an array of these instantiated components. Each element of the array is an
		 * object in the following format:
		 * {
		 *   component: *a reference to the component*
		 *   configuration: *the configuration used to create the component*
		 * }
		 */
		const container = {};

		/**
		 * Performs a comparison of two objects.
		 * **Currently performs a shallow comparison.**
		 *
		 * @param {Object} obj1 an object to compare
		 * @param {Object} obj2 an object to compare
		 * @returns {boolean} whether the two objects are equal
		 */
		const objectsAreEqual = (obj1, obj2) =>
			typeof obj1 === 'object' &&
			typeof obj2 === 'object' &&
			Object.keys(obj1).length === Object.keys(obj2).length &&
			Object.keys(obj1).every((key) => obj1[key] === obj2[key]);

		class RuntimeBuilder {
			/**
			 * The RuntimeBuilder is responsible for producing the runtime parts that ultimately result in a new
			 * InteractionListener based on the supplied configuration.
			 *
			 * @param {Object} thisWin The window object
			 * @param {Object} configuration The configuration object used to build the various runtime components.
			 * @constructor
			 *
			 * @see https://sourcemaking.com/design_patterns/builder
			 * @see tracking.framework.config.Main
			 */
			constructor(thisWin, configuration) {
				this.log = new com.dealer.log.Logger(
					'ws-tagging:RuntimeBuilder'
				);
				this.component =
					new win.DDC.tracking.framework.config.Component();
				this.win = thisWin;
				this.configuration = configuration;
				this.flags = configuration.flags;
				this.waitOnEventToStartHandling =
					configuration.waitOnEventToStartHandling;
			}

			/**
			 * Ensures the DataLayer data structure has been properly initialized
			 */
			buildDataLayer() {
				this.log.debug('building data layer...');
				// SMELL: seems like this should be done in ddc-data-layer widget
				this.win.dataLayer = this.win.dataLayer || [
					this.win.DDC.dataLayer
				];
				this.log.debug('data layer built.');
			}

			/**
			 * Builds a Page instance which will be shared among all the components
			 * that need one.
			 *
			 * @returns {tracking.framework.runtime.Page}
			 */
			buildPage() {
				this.log.debug('building page...');
				const page = this.component
					.withDefinition(this.configuration.page, {
						className: 'DDC.tracking.framework.runtime.Page'
					})
					.instantiate(this, function getNewpageClass(PageClass) {
						// The DDC datalayer is not available inside window.dataLayer at this point
						// So, we directly use DDC.dataLayer to build our DataLayerHelper.
						return new PageClass(
							new DataLayerHelper(
								this.flags[
									'google-tag-manager-move-below-js-stack'
								]
									? [this.win.DDC.dataLayer]
									: this.win.dataLayer
							)
						);
					});
				this.log.debug('page built.');
				return page;
			}

			/**
			 * Builds a Page instance which will be shared among all the components
			 * that need one.
			 *
			 * @returns {tracking.framework.runtime.Page}
			 */
			buildForm() {
				this.log.debug('building form...');
				const form = this.component
					.withDefinition(this.configuration.form, {
						className: 'DDC.tracking.framework.runtime.Form'
					})
					.instantiate(
						this,
						function getNewFormClass(FormClass) {
							return new FormClass(this.configuration.form);
						},
						false
					);
				this.log.debug('form built.');
				return form;
			}

			/**
			 * Builds the EventHandler implementation configured for the InteractionListener
			 * being constructed. This method instantiates and configures the EventHandler
			 * implementation and invokes the initialize() method if present.
			 *
			 * @param {tracking.framework.runtime.Page} page The Page instance for the EventHandler to use
			 * @returns {tracking.framework.runtime.EventHandler}
			 */
			buildEventHandler(page, form) {
				this.log.debug('building event handler...');
				const eventHandler = this.component
					.withDefinition(this.configuration.eventHandler, {
						initializer: 'initialize'
					})
					.instantiate(
						this,
						function getNewEventHandlerClass(EventHandlerClass) {
							return new EventHandlerClass(this.win, page, form);
						},
						false
					);
				this.log.debug('event handler built.');
				return eventHandler;
			}

			/**
			 * Instantiates and applies the configured EventHandlerDecorator implementations to the
			 * supplied EventHandler instance for the InteractionListener being built.
			 *
			 * EventHandlerDecorator instances follow the "Chain of Responsibility" design pattern,
			 * therefore this method must ensure that the decorators are applied in the correct order:
			 * The first decorator specified in the configuration will be the first to be invoked
			 * by the InteractionListener, while the last decorator specified will be invoked
			 * immediately before the actual EventHandler instance.
			 *
			 * @param {tracking.framework.runtime.Page} page The Page instance for the EventHandler to use
			 * @param {tracking.framework.runtime.EventHandler} eventHandler The instantiated EventHandler specified by configuration
			 * @returns {tracking.framework.runtime.EventHandler} The decorated EventHandler to be used by the InteractionListener
			 */
			buildDecoratorChain(page, eventHandler, form) {
				this.log.debug('building decorator chain...');
				if (this.configuration.decorators !== undefined) {
					for (
						let index = this.configuration.decorators.length - 1;
						index >= 0;
						index--
					) {
						// eslint-disable-next-line no-param-reassign
						eventHandler = this.component
							.withDefinition(
								this.configuration.decorators[index],
								undefined
							)
							.instantiate(
								this,
								// eslint-disable-next-line no-loop-func
								function geteventHandlerDecoratorClass(
									EventHandlerDecoratorClass
								) {
									return new EventHandlerDecoratorClass(
										this.win,
										page,
										eventHandler,
										form
									);
								},
								false
							);
					}
				}
				this.log.debug('decorator chain built.');
				return eventHandler;
			}

			/**
			 * Builds a new InteractionListener with the specified Page and EventHandler.
			 *
			 * @param {tracking.framework.runtime.Page} page The Page instance for the InteractionListener to use
			 * @param {tracking.framework.runtime.Form} form The Form instance for the InteractionListener to use
			 * @param {tracking.framework.runtime.EventHandler} eventHandler The instantiated EventHandler specified by configuration
			 * @returns {tracking.framework.runtime.InteractionListener}
			 */
			buildInteractionListener(page, eventHandler, form) {
				const config = this.configuration.interactionListener || {
					className:
						'DDC.tracking.framework.runtime.InteractionListener',
					initializer: 'initialize'
				};

				// attempt to retrieve identical interaction listener
				let listener = this.getComponent(config);

				if (typeof listener === 'undefined') {
					// build new interaction listener
					this.log.debug('building interaction listener...');
					listener = this.component
						.withDefinition(config)
						.instantiate(
							this,
							function getNewInteractionListenerClass(
								InteractionListenerClass
							) {
								return new InteractionListenerClass(
									this.win,
									page,
									eventHandler,
									form,
									this.flags,
									undefined,
									this.waitOnEventToStartHandling
								);
							},
							true
						);
					this.log.debug('interaction listener built.');

					// add listener to container
					this.setComponent(config, listener);
				} else {
					// add new event handler to interaction listener
					listener.addEventHandlers(eventHandler);
				}

				return listener;
			}

			/**
			 * Builds an array of {@link tracking.framework.runtime.Command} objects to be executed after the
			 * {@link tracking.framework.runtime.InteractionListener} has been initialized.
			 *
			 * @param {tracking.framework.runtime.Page} page The Page instance for the Command instances to use
			 * @param {tracking.framework.runtime.Form} form The Form instance for the Command instances to use
			 * @returns {Array} An array of {@link tracking.framework.runtime.Command} objects to be executed after the
			 *    {@link tracking.framework.runtime.InteractionListener} has been initialized.
			 */
			buildStartupCommands(page, eventHandler, form) {
				const commands = [];
				this.log.debug('building startup commands...');
				if (this.configuration.startupCommands !== undefined) {
					for (
						let index = 0;
						index < this.configuration.startupCommands.length;
						index++
					) {
						commands.push(
							this.component
								.withDefinition(
									this.configuration.startupCommands[index],
									undefined
								)
								.instantiate(
									this,
									function getNewCommandClass(CommandClass) {
										return new CommandClass(
											this.win,
											page,
											eventHandler,
											form
										);
									},
									false
								)
						);
					}
				}
				this.log.debug('startup commands built...');
				return commands;
			}

			/**
			 * Returns a component built using the provided configuration
			 *
			 * @param {Object} configuration the configuration of the requested component
			 * @returns {*} a component matching the requested configuration; undefined if not found
			 */
			getComponent(configuration) {
				const componentList =
					container[configuration && configuration.className];

				if (componentList) {
					// perform comparison of configs to determine if component is using identical configuration
					for (let i = 0; i < componentList.length; i++) {
						if (
							objectsAreEqual(
								configuration,
								componentList[i].configuration
							)
						) {
							this.log.debug(
								`retrieving identical ${configuration.className} component`
							);
							return componentList[i].component;
						}
					}
				}

				return undefined;
			}

			/**
			 * Returns a component built using the provided configuration
			 *
			 * @param {Object} configuration the configuration of the component to store
			 * @param {*} component the component built using the provided configuration
			 */
			setComponent(configuration, component) {
				if (configuration && configuration.className && component) {
					this.log.debug(
						`adding new ${configuration.className} component to container`
					);
					container[configuration.className] =
						container[configuration.className] || [];
					container[configuration.className].push({
						configuration,
						component
					});
				}
			}

			/**
			 * Retrieves container of components currently instantiated
			 *
			 * @returns {Object} the container of components
			 */
			static getContainer() {
				return container;
			}

			/**
			 * Removes container's references to built components
			 */
			static clearContainer() {
				Object.keys(container).forEach((key) => {
					delete container[key];
				});
				// for (const key in container) {
				// 	if (container.hasOwnProperty(key)) {
				// 		delete container[key];
				// 	}
				// }
			}
		}

		return RuntimeBuilder;
	})();
};

export default runRuntimeBuilder;
